import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsCurrentStepFormValid,
  setUseBillingForShipping,
} from "../../checkoutSlice";
import type { AppDispatch, RootState } from "../../../../app/store";
import type { TransformedAddress } from "../../../cart/types/cartApiTypes";
import type { TrCountry } from "../../../../types";
import type { TrCity, TrCityWithDistricts } from "../../types/checkoutApiTypes";

// --- Types ---
type ShippingFormData = Omit<
  TransformedAddress,
  "email" | "postcode" | "state"
> & {
  postcode?: string;
  state: string; // City/İl Name
  city: string; // District/İlçe Name
};

export interface ShippingAddressFormProps {
  initialData?: Partial<TransformedAddress>; // Initial state is WC code
  billingData?: Partial<TransformedAddress>; // Source for copying (state as code)
  onSubmit: (apiData: Partial<TransformedAddress>) => void;
  isSubmitting: boolean;
  formId: string;
  isDisabled: boolean;
  cities?: TrCity[];
  citiesWithDistricts?: TrCityWithDistricts[];
}

// --- Field Name Map ---
const fieldNameMap: { [key in keyof ShippingFormData]?: string } = {
  firstName: "checkout.fields.firstName",
  lastName: "checkout.fields.lastName",
  company: "checkout.fields.company",
  address1: "checkout.fields.address1",
  address2: "checkout.fields.address2",
  city: "checkout.fields.city", // District
  state: "checkout.fields.state", // City/Il
  postcode: "checkout.fields.postcode",
  country: "checkout.fields.country",
  phone: "checkout.fields.phone",
};

// --- Helper Functions ---
const findCityNameByCode = (
  code: string | undefined,
  cities: TrCity[] | undefined
): string => {
  if (!code || !cities) return "";
  return cities.find((c) => c.wcCode === code)?.name || "";
};

const findCodeByCityName = (
  name: string | undefined,
  cities: TrCity[] | undefined
): string => {
  if (!name || !cities) return "";
  return cities.find((c) => c.name === name)?.wcCode || "";
};

// --- Component ---
const ShippingAddressForm: React.FC<ShippingAddressFormProps> = ({
  initialData = {},
  billingData = {},
  onSubmit,
  isSubmitting,
  formId,
  isDisabled,
  cities = [],
  citiesWithDistricts = [],
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const currentStep = useSelector(
    (state: RootState) => state.checkout.currentStep
  );
  const trCountry = window.themeVariables?.trCountry as TrCountry | undefined;
  // console.log(`[ShippingForm Render] isDisabled: ${isDisabled}`);

  const sortedCities = useMemo(
    () =>
      [...(cities || [])].sort((a, b) => a.name.localeCompare(b.name, "tr")),
    [cities]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isValid /*, dirtyFields */ }, // Keep isValid
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<ShippingFormData>({
    mode: "onChange",
    defaultValues: {},
  });
  // console.log(`[ShippingForm Render] RHF isValid: ${isValid}`);

  const watchedState = watch("state");
  const watchedCity = watch("city");
  // console.log(`[ShippingForm Render] Watched - state: ${watchedState}, city: ${watchedCity}`);

  const hasPopulatedInitially = useRef(false);
  const prevIsDisabled = useRef(isDisabled);

  const sourceData = useMemo(() => {
    const data = isDisabled ? billingData : initialData;
    // console.log(`[ShippingForm Memo sourceData] isDisabled: ${isDisabled}, sourceData:`, data);
    return data && Object.keys(data).length > 0 ? data : {};
  }, [isDisabled, billingData, initialData]);

  // --- Effects ---

  // Effect 1: Populate/Reset Form Fields
  useEffect(() => {
    // console.log("[Effect 1 Ship - Populate/Reset START]", {isDisabled, hasPopulated: hasPopulatedInitially.current, prevIsDisabled: prevIsDisabled.current});
    const lookupDataIsReady =
      cities &&
      cities.length > 0 &&
      citiesWithDistricts &&
      citiesWithDistricts.length > 0;
    if (!lookupDataIsReady) {
      // console.log("[Effect 1 Ship - Populate/Reset] Skipping: Lookup data not ready.");
      prevIsDisabled.current = isDisabled;
      return;
    }

    if (!sourceData || Object.keys(sourceData).length === 0) {
      // console.log("[Effect 1 Ship - Populate/Reset] Source data is empty.");
      if (
        hasPopulatedInitially.current &&
        Object.keys(getValues()).length > 0
      ) {
        // console.log("[Effect 1 Ship - Populate/Reset] Clearing form because source data became empty.");
        reset({});
        hasPopulatedInitially.current = false;
      }
      prevIsDisabled.current = isDisabled;
      return;
    }

    const isDisabledChanged = prevIsDisabled.current !== isDisabled;
    const isInitialPopulation = !hasPopulatedInitially.current;

    let needsReset = false;
    let reason = "";

    if (isInitialPopulation) {
      needsReset = true;
      reason = "Initial population";
    } else if (isDisabledChanged) {
      needsReset = true;
      reason = `isDisabled changed`;
    }

    if (needsReset) {
      const sourceStateCode = sourceData.state;
      const targetStateName = findCityNameByCode(sourceStateCode, cities);
      const targetDistrictName = sourceData.city || "";
      let finalCityValue = "";
      if (targetStateName) {
        const targetCityData = citiesWithDistricts?.find(
          (c) => c.name === targetStateName
        );
        const districtIsValidForState = targetCityData?.districts.some(
          (d) => d.name === targetDistrictName
        );
        if (targetDistrictName && districtIsValidForState) {
          finalCityValue = targetDistrictName;
        }
      }
      const targetCountryName =
        trCountry && sourceData.country === trCountry.wcCode
          ? trCountry.name
          : sourceData.country || "";

      const dataToReset: Partial<ShippingFormData> = {
        firstName: sourceData.firstName || "",
        lastName: sourceData.lastName || "",
        company: sourceData.company || "",
        address1: sourceData.address1 || "",
        address2: sourceData.address2 || "",
        postcode: sourceData.postcode || "",
        phone: sourceData.phone || "",
        state: targetStateName || "",
        city: finalCityValue,
        country: targetCountryName,
      };

      // console.log(`[Effect 1 Ship - Populate/Reset] >>> Running reset. Reason: ${reason}. Data:`, dataToReset);
      reset(dataToReset, { keepDirty: false });

      if (isInitialPopulation) {
        hasPopulatedInitially.current = true;
        // console.log("[Effect 1 Ship - Populate/Reset] Initial population flag set.");
      }
    }
    // else {
    // console.log("[Effect 1 Ship - Populate/Reset] Skipping reset execution.");
    // if (isInitialPopulation) { hasPopulatedInitially.current = true; } // Still mark populated on first pass
    // }

    prevIsDisabled.current = isDisabled;
    // console.log("[Effect 1 Ship - Populate/Reset END]");
  }, [
    sourceData,
    isDisabled,
    reset,
    cities,
    citiesWithDistricts,
    trCountry,
    getValues,
  ]);

  // Effect 2: Set City based on Watched State and Source Data
  useEffect(() => {
    // console.log("[Effect 2 Ship - Set City START]", { watchedState, sourceData });
    if (
      !hasPopulatedInitially.current ||
      !watchedState ||
      !citiesWithDistricts ||
      citiesWithDistricts.length === 0
    ) {
      if (
        hasPopulatedInitially.current &&
        !watchedState &&
        getValues("city") !== ""
      ) {
        // console.log("[Effect 2 Ship - Set City] State empty, clearing city.");
        setValue("city", "", {
          shouldValidate: true,
          shouldDirty: !isDisabled,
        });
      }
      return;
    }

    const targetDistrictName = sourceData?.city || "";
    let cityValueToSet = "";

    const targetCityData = citiesWithDistricts.find(
      (c) => c.name === watchedState
    );
    const districtIsValidForState = targetCityData?.districts.some(
      (d) => d.name === targetDistrictName
    );

    if (targetDistrictName && districtIsValidForState) {
      cityValueToSet = targetDistrictName;
    }

    const currentFormCityValue = getValues("city");
    // console.log(`[Effect 2 Ship - Set City] Target: '${cityValueToSet}', Current: '${currentFormCityValue}'`);

    if (currentFormCityValue !== cityValueToSet) {
      // console.log(`[Effect 2 Ship - Set City] >>> Calling setValue('city', '${cityValueToSet}').`);
      setValue("city", cityValueToSet, {
        shouldValidate: true,
        shouldDirty: !isDisabled,
      });
    }
    // console.log("[Effect 2 Ship - Set City END]");
  }, [
    watchedState,
    sourceData,
    citiesWithDistricts,
    setValue,
    getValues,
    isDisabled,
  ]);

  // Effect 3: Clear City on Manual State Change
  useEffect(() => {
    // console.log("[Effect 3 Ship - Manual Clear START]", { watchedState, isDisabled });
    if (!hasPopulatedInitially.current || isDisabled) {
      // console.log("[Effect 3 Ship - Manual Clear] Skipping: Not init or disabled.");
      return;
    }

    const currentCity = getValues("city");
    const expectedStateName = findCityNameByCode(sourceData?.state, cities);
    // console.log(`[Effect 3 Ship - Manual Clear] Current city: '${currentCity}', Watched state: '${watchedState}', Expected state: '${expectedStateName}'`);

    if (watchedState !== expectedStateName && currentCity !== "") {
      // console.log("[Effect 3 Ship - Manual Clear] >>> State changed manually. Clearing city.");
      setValue("city", "", { shouldValidate: true, shouldDirty: true });
    }
    // console.log("[Effect 3 Ship - Manual Clear END]");
  }, [watchedState, setValue, getValues, isDisabled, sourceData, cities]);

  // Effect 4: Report Validity
  const reportValidity = useCallback(() => {
    const currentValues = getValues();
    const isStateSelected = !!currentValues.state?.trim();
    const isCitySelected = !!currentValues.city?.trim();
    const requiredFieldsFilled =
      isDisabled ||
      !!(
        isStateSelected &&
        isCitySelected &&
        currentValues.firstName?.trim() &&
        currentValues.lastName?.trim() &&
        currentValues.address1?.trim()
      );
    const calculatedValidity = isValid && requiredFieldsFilled;
    const stepValidity = isDisabled || calculatedValidity;

    // console.log(`[Effect 4 Ship - Report Validity] Final stepValidity: ${stepValidity}`);

    if (currentStep === "shipping") {
      // console.log(`[Effect 4 Ship - Report Validity] >>> Dispatching validity: ${stepValidity}`);
      dispatch(setIsCurrentStepFormValid(stepValidity));
    }
  }, [getValues, isValid, isDisabled, currentStep, dispatch]);

  useEffect(() => {
    // console.log("[Effect 4 Ship - Report Validity Trigger]");
    reportValidity();
  }, [reportValidity, watchedState, watchedCity, isValid]);

  // --- Submit Handler ---
  const handleInternalFormSubmit: SubmitHandler<ShippingFormData> = (
    formData
  ) => {
    // console.log("[ShippingForm Submit]", { isDisabled, formData });
    const stateCodeToSend = findCodeByCityName(formData.state, cities);
    const countryCodeToSend =
      trCountry && formData.country === trCountry.name
        ? trCountry.wcCode
        : trCountry && formData.country === trCountry.wcCode
        ? trCountry.wcCode
        : formData.country;

    const apiDataForContainer: Partial<TransformedAddress> = {
      ...formData,
      email: undefined,
      state: stateCodeToSend || formData.state,
      city: formData.city,
      country: countryCodeToSend,
    };
    // console.log("[ShippingForm Submit] Prepared apiData:", apiDataForContainer);
    onSubmit(apiDataForContainer);
  };

  // --- Error Rendering Helper ---
  const renderError = (fieldName: keyof ShippingFormData) => {
    if (isDisabled) return null;
    const error = errors[fieldName];
    if (!error) return null;
    const labelKey = fieldNameMap[fieldName];
    const translatedLabel = labelKey ? t(labelKey) : fieldName;
    let message = "";
    if (error.type === "required") {
      message = t("validation.required", { field: translatedLabel });
    } else if (error.message) {
      message = error.message;
    } else {
      message = t("validation.invalid", { field: translatedLabel });
    }
    return <span className="error-message">{message}</span>;
  };

  // --- Calculate District Options ---
  const districtOptions = useMemo(() => {
    if (
      !watchedState ||
      !citiesWithDistricts ||
      citiesWithDistricts.length === 0
    )
      return [];
    const selectedCityData = citiesWithDistricts.find(
      (c) => c.name === watchedState
    );
    if (!selectedCityData?.districts) return [];
    return [...selectedCityData.districts].sort((a, b) =>
      a.name.localeCompare(b.name, "tr")
    );
  }, [watchedState, citiesWithDistricts]);

  // console.log(`[ShippingForm Render] Rendering form. isDisabled: ${isDisabled}`);
  // --- JSX Render ---
  return (
    <form
      id={formId}
      onSubmit={handleSubmit(handleInternalFormSubmit)}
      className={`checkout-form-step shipping-address-form ${
        isDisabled ? "is-disabled" : ""
      }`}
      noValidate
    >
      {/* Checkbox */}
      <div className="form-field full-width use-billing-for-shipping">
        <label htmlFor={`${formId}-use-billing-for-shipping`}>
          <input
            type="checkbox"
            id={`${formId}-use-billing-for-shipping`}
            checked={isDisabled}
            onChange={(e) =>
              dispatch(setUseBillingForShipping(e.target.checked))
            }
          />
          {t("checkout.useBillingForShipping")}
        </label>
      </div>

      {/* Fields Container */}
      <div className="shipping-fields-container">
        <div className="form-grid">
          {/* First Name */}
          <div className="form-field">
            <label htmlFor={`${formId}-firstName`}>
              {t("checkout.fields.firstName")}
            </label>
            <input
              type="text"
              id={`${formId}-firstName`}
              {...register("firstName", { required: !isDisabled })}
              disabled={isDisabled || isSubmitting}
              autoComplete="shipping given-name"
              aria-invalid={errors.firstName ? "true" : "false"}
            />
            {renderError("firstName")}
          </div>

          {/* Last Name */}
          <div className="form-field">
            <label htmlFor={`${formId}-lastName`}>
              {t("checkout.fields.lastName")}
            </label>
            <input
              type="text"
              id={`${formId}-lastName`}
              {...register("lastName", { required: !isDisabled })}
              disabled={isDisabled || isSubmitting}
              autoComplete="shipping family-name"
              aria-invalid={errors.lastName ? "true" : "false"}
            />
            {renderError("lastName")}
          </div>

          {/* Company */}
          <div className="form-field full-width">
            <label htmlFor={`${formId}-company`}>{`${t(
              "checkout.fields.company"
            )} ${t("common.optional")}`}</label>
            <input
              type="text"
              id={`${formId}-company`}
              {...register("company")}
              disabled={isDisabled || isSubmitting}
              autoComplete="shipping organization"
            />
            {renderError("company")}
          </div>

          {/* Country */}
          <div className="form-field">
            <label htmlFor={`${formId}-country`}>
              {t("checkout.fields.country")}
            </label>
            <input
              type="text"
              id={`${formId}-country`}
              {...register("country", { required: !isDisabled })}
              disabled={true} // Always disabled for TR
              autoComplete="shipping country"
              aria-invalid={errors.country ? "true" : "false"}
              placeholder={trCountry?.name || t("checkout.fields.country")}
              className="country-input-fixed"
            />
            {renderError("country")}
          </div>

          {/* State (City/İl) Dropdown */}
          <div className="form-field">
            <label htmlFor={`${formId}-state`}>
              {t("checkout.fields.state")}
            </label>
            <select
              id={`${formId}-state`}
              {...register("state", { required: !isDisabled })}
              disabled={isDisabled || isSubmitting}
              autoComplete="shipping address-level1"
              aria-invalid={errors.state ? "true" : "false"}
            >
              <option value="">{t("checkout.fields.selectState")}</option>
              {sortedCities.map((city) => (
                <option key={city.wcCode} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>
            {renderError("state")}
          </div>

          {/* City (District/İlçe) Dropdown */}
          <div className="form-field">
            <label htmlFor={`${formId}-city`}>
              {t("checkout.fields.city")}
            </label>
            <select
              id={`${formId}-city`}
              {...register("city", { required: !isDisabled })}
              disabled={
                isDisabled ||
                isSubmitting ||
                !watchedState ||
                districtOptions.length === 0
              }
              autoComplete="shipping address-level2"
              aria-invalid={errors.city ? "true" : "false"}
            >
              <option value="">
                {districtOptions.length === 0 && watchedState
                  ? t("checkout.fields.noDistricts")
                  : t("checkout.fields.selectDistrict")}
              </option>
              {districtOptions.map((district) => (
                <option key={district.id} value={district.name}>
                  {district.name}
                </option>
              ))}
            </select>
            {renderError("city")}
          </div>

          {/* Postcode */}
          <div className="form-field">
            <label htmlFor={`${formId}-postcode`}>{`${t(
              "checkout.fields.postcode"
            )} ${t("common.optional")}`}</label>
            <input
              type="text"
              id={`${formId}-postcode`}
              {...register("postcode")}
              disabled={isDisabled || isSubmitting}
              autoComplete="shipping postal-code"
              aria-invalid={errors.postcode ? "true" : "false"}
            />
            {renderError("postcode")}
          </div>

          {/* Address 1 */}
          <div className="form-field full-width">
            <label htmlFor={`${formId}-address1`}>
              {t("checkout.fields.address1")}
            </label>
            <input
              type="text"
              id={`${formId}-address1`}
              {...register("address1", { required: !isDisabled })}
              disabled={isDisabled || isSubmitting}
              placeholder={t("checkout.fields.address1Placeholder")}
              autoComplete="shipping address-line1"
              aria-invalid={errors.address1 ? "true" : "false"}
            />
            {renderError("address1")}
          </div>

          {/* Address 2 */}
          <div className="form-field full-width">
            <label htmlFor={`${formId}-address2`}>{`${t(
              "checkout.fields.address2"
            )} ${t("common.optional")}`}</label>
            <input
              type="text"
              id={`${formId}-address2`}
              {...register("address2")}
              disabled={isDisabled || isSubmitting}
              placeholder={t("checkout.fields.address2Placeholder")}
              autoComplete="shipping address-line2"
            />
            {renderError("address2")}
          </div>

          {/* Phone */}
          <div className="form-field full-width">
            <label htmlFor={`${formId}-phone`}>{`${t(
              "checkout.fields.phone"
            )} ${t("common.optional")}`}</label>
            <input
              type="tel"
              id={`${formId}-phone`}
              {...register("phone")}
              disabled={isDisabled || isSubmitting}
              autoComplete="shipping tel"
              aria-invalid={errors.phone ? "true" : "false"}
            />
            {renderError("phone")}
          </div>
        </div>
      </div>
    </form>
  );
};

export default ShippingAddressForm;
